/**
 * "N things to check" (bug fix 1, spec 6 §2): each doubt as a question about one page, with
 * the page's picture and opening words already loaded, and two answers. Answering removes
 * the card; none left reads "All checked ✓". Questions never block "Make flashcards".
 */
import { useEffect, useState } from "react";
import { chapterTitle } from "../papers/chapters";
import type { Question, Reply } from "./questions";
import { Thumb, type Previewer } from "./Thumb";
import type { DetectedChapter } from "./types";

type Props = {
  questions: Question[];
  /** Chapters by number, for a card's title and its page's opening words. */
  chapters: Map<number, DetectedChapter>;
  onAnswer: (q: Question, reply: Reply) => void;
  onTitle: (chapter: number, title: string) => void;
  onJump?: (page: number) => Promise<string | null>;
  onPreview?: Previewer;
  /** Whether there were ever questions, so an empty block can say they are all answered. */
  asked: boolean;
};

/** The page's opening words: the excerpt sent with the chapter, else read on demand. */
function Opening({
  page,
  known,
  onJump,
}: {
  page: number;
  known?: string | null;
  onJump?: (page: number) => Promise<string | null>;
}) {
  const [text, setText] = useState<string | null | undefined>(known ?? undefined);
  useEffect(() => {
    if (known || !onJump) return;
    let live = true;
    void Promise.resolve(onJump(page)).then((t) => live && setText(t ?? null));
    return () => {
      live = false;
    };
  }, [page, known, onJump]);
  if (!text) return null;
  return <blockquote className="question-card__words">“{text}”</blockquote>;
}

function StartCard({ q, props }: { q: Extract<Question, { kind: "start" }>; props: Props }) {
  const [page, setPage] = useState("");
  const [moving, setMoving] = useState(false);
  const c = props.chapters.get(q.chapter);
  return (
    <>
      <p className="question-card__ask">
        Does chapter {q.chapter}, <em>{chapterTitle(q.title)}</em>, start on this page (page{" "}
        {q.page})?
        {q.counter && (
          <span className="question-card__counter">
            {" "}
            {q.counter.at} of {q.counter.of}
          </span>
        )}
      </p>
      <div className="question-card__page">
        <Thumb page={q.page} onPreview={props.onPreview} caption={`Page ${q.page}`} />
        <Opening page={q.page} known={c?.excerpt} onJump={props.onJump} />
      </div>
      <div className="question-card__answers">
        <button type="button" className="button" onClick={() => props.onAnswer(q, { kind: "yes" })}>
          Yes
        </button>
        {moving ? (
          <span className="question-card__move">
            <label>
              It starts on page{" "}
              <input
                type="number"
                className="chapter__page-input"
                value={page}
                aria-label={`Chapter ${q.chapter} starts on page`}
                onChange={(e) => setPage(e.target.value)}
              />
            </label>
            <button
              type="button"
              className="button button--primary"
              disabled={page === ""}
              onClick={() => props.onAnswer(q, { kind: "startsOn", page: Number(page) })}
            >
              Set
            </button>
          </span>
        ) : (
          <button type="button" className="button" onClick={() => setMoving(true)}>
            No, it starts on another page
          </button>
        )}
      </div>
    </>
  );
}

function TitleCard({ q, props }: { q: Extract<Question, { kind: "title" }>; props: Props }) {
  const [title, setTitle] = useState("");
  return (
    <>
      <p className="question-card__ask">What is chapter {q.chapter} called?</p>
      <div className="question-card__answers">
        <input
          aria-label={`Title of chapter ${q.chapter}`}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <button
          type="button"
          className="button button--primary"
          disabled={title.trim() === ""}
          onClick={() => props.onTitle(q.chapter, title.trim())}
        >
          Save
        </button>
      </div>
    </>
  );
}

function Card({ q, props }: { q: Question; props: Props }) {
  switch (q.kind) {
    case "book":
      return (
        <>
          <p className="question-card__ask">
            Do these pages each start the chapter named under them?
          </p>
          <div className="question-card__samples">
            {q.samples.map((c) => (
              <Thumb
                key={c.number}
                page={c.startPage}
                onPreview={props.onPreview}
                caption={`Chapter ${c.number} · ${chapterTitle(c.title)} · page ${c.startPage}`}
              />
            ))}
          </div>
          <div className="question-card__answers">
            <button
              type="button"
              className="button button--primary"
              onClick={() => props.onAnswer(q, { kind: "yes" })}
            >
              Yes, all of them
            </button>
            <button
              type="button"
              className="button"
              onClick={() => props.onAnswer(q, { kind: "no" })}
            >
              No
            </button>
          </div>
        </>
      );
    case "start":
      return <StartCard q={q} props={props} />;
    case "title":
      return <TitleCard q={q} props={props} />;
    case "gap":
      return (
        <>
          <p className="question-card__ask">
            Pages {q.startPage}–{q.endPage} aren’t in any chapter. Should they be?
          </p>
          <div className="question-card__page">
            <Thumb page={q.startPage} onPreview={props.onPreview} caption={`Page ${q.startPage}`} />
            <Opening page={q.startPage} onJump={props.onJump} />
          </div>
          <div className="question-card__answers">
            <button
              type="button"
              className="button"
              onClick={() => props.onAnswer(q, { kind: "leaveOut" })}
            >
              No, leave them out
            </button>
            <button
              type="button"
              className="button"
              onClick={() => props.onAnswer(q, { kind: "addTo", chapter: q.before })}
            >
              Add them to chapter {q.before}
            </button>
            <button
              type="button"
              className="button"
              onClick={() => props.onAnswer(q, { kind: "addTo", chapter: q.after })}
            >
              Add them to chapter {q.after}
            </button>
          </div>
        </>
      );
  }
}

export function QuestionCards(props: Props) {
  const { questions, asked } = props;
  if (!asked) return null;
  const n = questions.length;
  return (
    <section className="questions" aria-labelledby="questions-title">
      <h2 id="questions-title" className="questions__title">
        {n === 0 ? "All checked ✓" : `${n} thing${n === 1 ? "" : "s"} to check`}
      </h2>
      {questions.map((q) => (
        <article key={q.id} id={`question-${q.id}`} className="question-card" aria-label={q.id}>
          <Card q={q} props={props} />
        </article>
      ))}
    </section>
  );
}
